import type { PluginSettingsSection, PluginWebManifest } from '@reflex/plugin-api/web';
import { REFERRALS_PAGE_PATH } from './shared/types.js';

const REFERRALS_LABEL = 'Share Reflex';
const REFERRALS_ICON = 'Share2';

/**
 * The "Share Reflex" card. Settings and profile contribute the identical
 * section, so it is declared once here and referenced from both lists rather
 * than copied, which is how the two drift apart.
 */
const shareReflexSection: PluginSettingsSection = {
  id: 'referrals-share-reflex',
  label: REFERRALS_LABEL,
  icon: REFERRALS_ICON,
  componentPath: '@reflex/plugin-referrals/web/ShareReflexCard',
  order: 40,
};

/**
 * Server-free web manifest, imported by `web/register.ts` — never import
 * `../index.js` from web code (it drags the server graph into Vite).
 */
export const referralsWeb: PluginWebManifest = {
  routes: [
    {
      // Public chromeless landing page for `/i/<code>` short links. It asks
      // the plugin's public resolve route whether the code is real (which
      // records the click), stashes it for the claimer, then forwards the
      // visitor on. Nothing but the code is ever in the URL.
      path: '/i/:code',
      componentPath: '@reflex/plugin-referrals/web/ReferralLandingPage',
      chromeless: true,
      public: true,
    },
    {
      // A person's link and the accounts it brought in, both of which follow
      // the person rather than an organization — so the page sits at the root
      // alongside /profile and /settings rather than under an org slug.
      path: REFERRALS_PAGE_PATH,
      componentPath: '@reflex/plugin-referrals/web/ReferralsPage',
      rootLevel: true,
    },
  ],
  navItems: [
    {
      to: REFERRALS_PAGE_PATH,
      label: REFERRALS_LABEL,
      icon: REFERRALS_ICON,
      group: 'user-menu',
      rootLevel: true,
      order: 80,
    },
  ],
  commands: [
    {
      to: REFERRALS_PAGE_PATH,
      label: REFERRALS_LABEL,
      icon: REFERRALS_ICON,
      group: 'settings',
      rootLevel: true,
    },
  ],
  settingsSections: [shareReflexSection],
  profileSections: [shareReflexSection],
  globalUiElements: [
    {
      // Renders nothing, ever. It exists because the code a visitor arrived on
      // can only be credited once they have an account, and the first authed
      // page they see is the earliest moment that is true — whichever page the
      // host sends them to, and whichever provider they signed in with.
      id: 'referrals-claimer',
      componentPath: '@reflex/plugin-referrals/web/ReferralClaimer',
    },
  ],
};
